import React, { useState, useEffect } from 'react';
import { Stack, Button, TextField } from '@mui/material';
import StepSection from './StepSection';
import MethodSelect from './MethodSelect';
import FieldAutocomplete from './FieldAutocomplete';
import ChipList from './ChipList';
import { DIVERSITY_METHODS } from './constants';
import { buildDiversityConfig } from './rankingConfigBuilders';

const defaultDiversityState = {
  enabled: false,
  method: 'fields',
  fieldInput: '',
  diversityFields: [],
  semanticLambda: 0.5,
  semanticHorizon: 20,
};

export default function DiversityStep({ onConfigChange, availableFields }) {
  const [state, setState] = useState(defaultDiversityState);

  useEffect(() => {
    const config = buildDiversityConfig(state);
    onConfigChange?.({ enabled: state.enabled, config });
  }, [state, onConfigChange]);

  const update = (partial) => setState((prev) => ({ ...prev, ...partial }));

  const addField = () => {
    const field = (state.fieldInput && state.fieldInput.trim()) || state.fieldInput;
    if (!field || state.diversityFields.includes(field)) return;
    setState((prev) => ({
      ...prev,
      diversityFields: [...prev.diversityFields, field],
      fieldInput: '',
    }));
  };

  return (
    <StepSection
      title="2) Diversity"
      enabled={state.enabled}
      onEnabledChange={(enabled) => update({ enabled })}
    >
      <>
        <MethodSelect
          options={DIVERSITY_METHODS}
          value={state.method}
          onChange={(method) => update({ method })}
        />

        {state.method === 'fields' && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <FieldAutocomplete
                options={availableFields}
                value={state.fieldInput}
                onChange={(fieldInput) => update({ fieldInput })}
                label="Field"
                sx={{ flex: 1 }}
              />
              <Button size="small" variant="outlined" onClick={addField}>
                Add
              </Button>
            </Stack>
            <ChipList
              items={state.diversityFields}
              onDelete={(idx) =>
                setState((prev) => ({
                  ...prev,
                  diversityFields: prev.diversityFields.filter((_, i) => i !== idx),
                }))
              }
            />
          </Stack>
        )}

        {state.method === 'semantic' && (
          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              type="number"
              label="lambda"
              value={state.semanticLambda}
              onChange={(e) => update({ semanticLambda: e.target.value })}
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="horizon"
              value={state.semanticHorizon}
              onChange={(e) => update({ semanticHorizon: e.target.value })}
              inputProps={{ min: 5 }}
              fullWidth
            />
          </Stack>
        )}
      </>
    </StepSection>
  );
}
